import * as React from 'react';
import { Form, Formik } from 'formik';
import Box from '@mui/material/Box';
import { LoadingButton } from '@mui/lab';
import { Stack, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { IdentificationType } from '@prisma/client';
import { ValidationError } from 'yup';
import {
  enabledIdentificationTypesSchema,
  GetWaiversData,
  IDUploadValues,
} from '../../../types/waivers';
import useUpdateWaiver from '../../../hooks/waivers/useUpdateWaiver';
import '../../form/form.css';
import MultiSwitchInput from '../../form/base/MultiSwitchInput';

interface Props {
  onSuccess?: () => void;
  draft: GetWaiversData | null | undefined;
}

export default function IDUploadForm({ onSuccess, draft }: Props) {
  const [formError, setFormError] = React.useState('');

  const { mutation, isPending } = useUpdateWaiver({ draft });

  const [initialFormState] = React.useState<IDUploadValues>({
    types: draft?.identificationSettings?.types
      ?.filter(t => t.enabled)
      .map(t => t.type) ?? [IdentificationType.ID],
  });

  const onSubmit = async (values: IDUploadValues) => {
    if (!draft?.businessId || isPending) return;
    try {
      setFormError('');

      const identificationSettings: Parameters<
        typeof mutation.mutateAsync
      >[0]['identificationSettings'] = {
        types: Object.values(IdentificationType).map(t => ({
          type: t,
          enabled: values.types.some(v => v === t),
        })),
      };

      const response = await mutation.mutateAsync({
        identificationSettings,
      });

      if (response) {
        toast.success('ID upload settings saved successfully');
        onSuccess?.();
      }
    } catch (error) {
      if (error instanceof ValidationError) {
        setFormError(error.message);
      } else {
        toast.error('Failed to update ID upload settings');
      }
    }
  };

  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={initialFormState}
        validationSchema={enabledIdentificationTypesSchema}
        onSubmit={onSubmit}>
        {({ isSubmitting, isValid, isValidating, dirty }) => (
          <Form>
            {formError && (
              <Typography variant="body1" color="primary">
                {formError}
              </Typography>
            )}
            <Stack width="100%" spacing={2.5}>
              <MultiSwitchInput
                name="types"
                label="Select which types of ID you require clients to upload:"
                options={[
                  {
                    title: 'Drivers License or ID',
                    label: 'Require a drivers license or ID',
                    value: 'ID',
                  },
                  {
                    title: 'Birth Certificate',
                    label: 'Require a birth certificate',
                    value: 'BIRTH_CERTIFICATE',
                  },
                  {
                    title: 'Parent or Guardian Drivers License or ID',
                    label:
                      'Require a parent or guardians drivers license or ID',
                    value: 'GUARDIAN_ID',
                  },
                ]}
                required
              />
            </Stack>

            <LoadingButton
              type="submit"
              disabled={
                !isValid || isSubmitting || !dirty || isValidating || isPending
              }
              variant="contained"
              className="primary"
              loading={isSubmitting}>
              <span>Save</span>
            </LoadingButton>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
